import React, { useState, useRef, useEffect } from 'react';
import * as S from './Footer.style';

const CONTACT_EMAIL = 'hello@semn.ai';
const EFFECTIVE_DATE = 'February 6, 2026';

const TermsContent: React.FC = () => (
  <>
    <S.PopoverTitle>Terms of service</S.PopoverTitle>
    <S.EffectiveDate>Effective date: {EFFECTIVE_DATE}</S.EffectiveDate>

    <S.SectionTitle>1. What this service is</S.SectionTitle>
    <S.Paragraph>
      semn.ai provides real-time market data, visualizations, and contextual analysis intended to
      support understanding of market conditions.
    </S.Paragraph>
    <S.Paragraph>The service is informational only.</S.Paragraph>
    <S.Paragraph>
      It does not provide financial advice, investment recommendations, trading instructions, or
      guarantees of any outcome.
    </S.Paragraph>

    <S.SectionTitle>2. What this service is not</S.SectionTitle>
    <S.Paragraph>semn.ai does not:</S.Paragraph>
    <S.List>
      <S.ListItem>provide buy or sell signals</S.ListItem>
      <S.ListItem>recommend specific trades, positions, or leverage</S.ListItem>
      <S.ListItem>execute trades on your behalf</S.ListItem>
      <S.ListItem>guarantee accuracy, profitability, or performance</S.ListItem>
    </S.List>
    <S.Paragraph>
      All decisions you make using information from semn.ai are your own responsibility.
    </S.Paragraph>

    <S.SectionTitle>3. User responsibility</S.SectionTitle>
    <S.Paragraph>You acknowledge that:</S.Paragraph>
    <S.List>
      <S.ListItem>financial markets involve risk</S.ListItem>
      <S.ListItem>past or current observations do not predict future outcomes</S.ListItem>
      <S.ListItem>
        you are solely responsible for any actions taken based on the information provided
      </S.ListItem>
    </S.List>
    <S.Paragraph>
      You should use semn.ai as a contextual tool, not as a decision authority.
    </S.Paragraph>

    <S.SectionTitle>4. Access levels</S.SectionTitle>
    <S.Paragraph>semn.ai may offer different access levels (preview, trial, paid).</S.Paragraph>
    <S.Paragraph>Access levels may limit:</S.Paragraph>
    <S.List>
      <S.ListItem>duration of access</S.ListItem>
      <S.ListItem>data frequency</S.ListItem>
      <S.ListItem>available features</S.ListItem>
    </S.List>
    <S.Paragraph>These limits may change over time.</S.Paragraph>

    <S.SectionTitle>5. Subscriptions and payments</S.SectionTitle>
    <S.Paragraph>Paid access is offered on a subscription basis.</S.Paragraph>
    <S.Paragraph>
      Payments are processed by Stripe. semn.ai does not store your payment details.
    </S.Paragraph>
    <S.Paragraph>Subscriptions:</S.Paragraph>
    <S.List>
      <S.ListItem>renew automatically unless canceled</S.ListItem>
      <S.ListItem>can be canceled at any time</S.ListItem>
      <S.ListItem>remain active until the end of the current billing period</S.ListItem>
    </S.List>
    <S.Paragraph>Refunds, if any, are handled according to Stripe&apos;s policies.</S.Paragraph>

    <S.SectionTitle>6. Availability and changes</S.SectionTitle>
    <S.Paragraph>
      The service is provided {'"'}as is{'"'} and {'"'}as available{'"'}.
    </S.Paragraph>
    <S.Paragraph>We may:</S.Paragraph>
    <S.List>
      <S.ListItem>modify features</S.ListItem>
      <S.ListItem>update data sources</S.ListItem>
      <S.ListItem>suspend or discontinue parts of the service</S.ListItem>
    </S.List>
    <S.Paragraph>without prior notice.</S.Paragraph>

    <S.SectionTitle>7. Limitation of liability</S.SectionTitle>
    <S.Paragraph>To the fullest extent permitted by law, semn.ai is not liable for:</S.Paragraph>
    <S.List>
      <S.ListItem>financial losses</S.ListItem>
      <S.ListItem>trading outcomes</S.ListItem>
      <S.ListItem>decisions made using the service</S.ListItem>
    </S.List>
    <S.Paragraph>Your use of the service is at your own risk.</S.Paragraph>

    <S.SectionTitle>8. Contact</S.SectionTitle>
    <S.Paragraph>For questions about these terms, contact: {CONTACT_EMAIL}</S.Paragraph>
  </>
);

const PrivacyContent: React.FC = () => (
  <>
    <S.PopoverTitle>Privacy policy</S.PopoverTitle>
    <S.EffectiveDate>Effective date: {EFFECTIVE_DATE}</S.EffectiveDate>

    <S.SectionTitle>1. What we collect</S.SectionTitle>
    <S.Paragraph>We collect only what is necessary to operate the service:</S.Paragraph>
    <S.List>
      <S.ListItem>Email address (for authentication via magic links)</S.ListItem>
      <S.ListItem>Account access state (trial, paid, expiration)</S.ListItem>
      <S.ListItem>Basic usage information (to operate and secure the service)</S.ListItem>
    </S.List>
    <S.Paragraph>We do not collect:</S.Paragraph>
    <S.List>
      <S.ListItem>personal financial information</S.ListItem>
      <S.ListItem>trading accounts or credentials</S.ListItem>
    </S.List>

    <S.SectionTitle>2. Payments</S.SectionTitle>
    <S.Paragraph>Payments are processed by Stripe.</S.Paragraph>
    <S.Paragraph>semn.ai does not store:</S.Paragraph>
    <S.List>
      <S.ListItem>credit card numbers</S.ListItem>
      <S.ListItem>billing details</S.ListItem>
    </S.List>
    <S.Paragraph>Stripe&apos;s privacy policy applies to all payment data.</S.Paragraph>

    <S.SectionTitle>3. How we use your data</S.SectionTitle>
    <S.Paragraph>We use your data to:</S.Paragraph>
    <S.List>
      <S.ListItem>authenticate access</S.ListItem>
      <S.ListItem>manage subscriptions</S.ListItem>
      <S.ListItem>operate and improve the service</S.ListItem>
      <S.ListItem>prevent abuse</S.ListItem>
    </S.List>
    <S.Paragraph>We do not sell or rent your data.</S.Paragraph>

    <S.SectionTitle>4. Cookies</S.SectionTitle>
    <S.Paragraph>semn.ai uses cookies only for:</S.Paragraph>
    <S.List>
      <S.ListItem>authentication</S.ListItem>
      <S.ListItem>session management</S.ListItem>
    </S.List>
    <S.Paragraph>No tracking or advertising cookies are used.</S.Paragraph>

    <S.SectionTitle>5. Data retention</S.SectionTitle>
    <S.Paragraph>We retain data only as long as necessary to:</S.Paragraph>
    <S.List>
      <S.ListItem>provide the service</S.ListItem>
      <S.ListItem>comply with legal obligations</S.ListItem>
    </S.List>
    <S.Paragraph>You may request deletion of your account and associated data.</S.Paragraph>

    <S.SectionTitle>6. Third parties</S.SectionTitle>
    <S.Paragraph>We share data only with:</S.Paragraph>
    <S.List>
      <S.ListItem>Stripe (payments)</S.ListItem>
      <S.ListItem>essential infrastructure providers (hosting, email delivery)</S.ListItem>
    </S.List>
    <S.Paragraph>Only what is required is shared.</S.Paragraph>

    <S.SectionTitle>7. Your rights</S.SectionTitle>
    <S.Paragraph>You may:</S.Paragraph>
    <S.List>
      <S.ListItem>request access to your data</S.ListItem>
      <S.ListItem>request correction or deletion</S.ListItem>
    </S.List>
    <S.Paragraph>Contact: {CONTACT_EMAIL}</S.Paragraph>

    <S.SectionTitle>8. Changes</S.SectionTitle>
    <S.Paragraph>This policy may be updated as the service evolves.</S.Paragraph>
    <S.Paragraph>Material changes will be reflected on this page.</S.Paragraph>
  </>
);

type PopoverType = 'terms' | 'privacy' | null;

export const Footer: React.FC = () => {
  const year = new Date().getFullYear();
  const [activePopover, setActivePopover] = useState<PopoverType>(null);
  const termsRef = useRef<HTMLDivElement>(null);
  const privacyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        termsRef.current &&
        !termsRef.current.contains(target) &&
        privacyRef.current &&
        !privacyRef.current.contains(target)
      ) {
        setActivePopover(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggle = (type: PopoverType) => {
    setActivePopover((prev) => (prev === type ? null : type));
  };

  return (
    <S.FooterContainer>
      <S.PopoverWrapper ref={termsRef}>
        <S.FooterLink onClick={() => toggle('terms')}>[ terms ]</S.FooterLink>
        {activePopover === 'terms' && (
          <S.Popover onClick={(e) => e.stopPropagation()}>
            <TermsContent />
          </S.Popover>
        )}
      </S.PopoverWrapper>

      <S.PopoverWrapper ref={privacyRef}>
        <S.FooterLink onClick={() => toggle('privacy')}>[ privacy ]</S.FooterLink>
        {activePopover === 'privacy' && (
          <S.Popover onClick={(e) => e.stopPropagation()}>
            <PrivacyContent />
          </S.Popover>
        )}
      </S.PopoverWrapper>

      <S.FooterLink as="a" href="https://semn.ai/blog">
        [ insights ]
      </S.FooterLink>
      <S.FooterLink as="a" href={`mailto:${CONTACT_EMAIL}`}>
        [ contact ]
      </S.FooterLink>
      <S.Copyright>&copy; {year} semn.ai &middot; all rights reserved</S.Copyright>
    </S.FooterContainer>
  );
};
